import React from 'react';

import { DNABox, DNAButton, DNAModal, DNAText, GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui';
import { DNAModalVariant } from './DNAConnectedModal';
import { useUsersByTenantId } from 'src/state/redux/selector/user';
import { useTenant } from 'src/state/redux/selector/tenant';
import { getExceededLicenseType } from 'src/utils/licenseValidation';

export enum UserReactivateMode {
  reactivate = 'reactivate',
  deactivate = 'deactivate'
}

interface UserReactivateConfirmationProps {
  onClose: () => void
  mode: UserReactivateMode,
  username: string,
  tenantId: string,
  userId: string,
}

const reactivateText = {
  title: {
    reactivate: 'Reactivate User',
    deactivate: 'Deactivate User',
  },
  body: {
    reactivate: 'Are you sure you want to reactivate',
    deactivate: 'Are you sure you want to deactivate',
  },
}

const UserReactivateConfirmation:DNAModalVariant<UserReactivateConfirmationProps> = (props) => {
  const { toggleModal, onClose, mode, username } = props
  const tenantUsers = useUsersByTenantId(props.tenantId);
  const tenant = useTenant(props.tenantId);
  const toast = useToast();

  const handleContinue = () => {
    const userToReactivate = tenantUsers.find((u) => u.model.id === props.userId)?.model;

    if (mode === UserReactivateMode.reactivate && userToReactivate) {
      const exceededLicenseType = getExceededLicenseType(
        tenantUsers.map((tenantUser) => tenantUser.model),
        { ...userToReactivate, status: 'ACTIVE' },
        {
          standard: tenant?.tenant.licensedUsers,
          viewOnly: tenant?.tenant.licensedViewOnlyUsers,
        },
        userToReactivate,
      );

      if (exceededLicenseType) {
        const title = exceededLicenseType === 'VIEW_ONLY'
          ? 'View-only user limit has been reached'
          : 'User limit has been reached';

        toast?.add(
          <GenericToast title={title} status="error" />,
          ToastOrientations.TOP_RIGHT,
        );
        toggleModal();
        return;
      }
    }

    toggleModal()
    onClose()
  }

  const handleCancel = () => {
    toggleModal()
  }

  return (
    <DNAModal>
      <DNAModal.Header>
        <DNAText h5>{reactivateText.title[mode]}</DNAText>
      </DNAModal.Header>
      <DNAModal.Body>
        <DNABox style={{ padding: 8 }}>
          <DNAText status="tertiary">
            {`${reactivateText.body[mode]} ${username}?`}
          </DNAText>
        </DNABox>
      </DNAModal.Body>
      <DNAModal.Confirmation>
        <DNAButton status="tertiary" onPress={handleCancel}>
          Cancel
        </DNAButton>
        <DNAButton status="primary" onPress={handleContinue}>
          {mode === UserReactivateMode.deactivate ? 'Deactivate' : 'Reactivate'}
        </DNAButton>
      </DNAModal.Confirmation>
    </DNAModal>
  )
};

export default UserReactivateConfirmation;
